import { Box, Grid, Paper, Typography, useTheme } from "@mui/material";
import { DataLayout, DonutChart } from "../../../shared/components";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
} from "recharts";

interface VolumeData {
    week: string;
    materials: number;
    resources: number;
}

interface LeadTimeData {
    type: string;
    days: number;
}

const KpiCard = ({ title, value, subtitle, variance }: { title: string; value: string; subtitle: string; variance: string }) => {
    return (
        <Paper
            elevation={3}
            sx={{ padding: 2, width: '100%' }}
            data-background-elevation={1}
            variant="outlined"
        >
            <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} gap={1}>
                <Typography variant="caption" color="text.secondary">{title}</Typography>
                <Typography variant="body1" color="text.primary">{value}</Typography>
                <Box display={'flex'} alignItems={'center'} gap={1}>
                    <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
                    <Typography variant="caption" color="success.main">{variance}</Typography>
                </Box>
            </Box>
        </Paper>
    );
};

const RequestVolumeTrend = () => {
    const data: VolumeData[] = [
        { week: "Week 1", materials: 12, resources: 8 },
        { week: "Week 2", materials: 18, resources: 12 },
        { week: "Week 3", materials: 15, resources: 10 },
        { week: "Week 4", materials: 22, resources: 15 },
    ];
    return (
        <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                    type="monotone"
                    dataKey="materials"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot
                    name="Materials"
                />
                <Line
                    type="monotone"
                    dataKey="resources"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    dot
                    name="Resources"
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

const AverageLeadTimeChart = () => {
    const data: LeadTimeData[] = [
        { type: "Pipe", days: 9 },
        { type: "Valves", days: 12 },
        { type: "Fittings", days: 7 },
        { type: "Excavator", days: 4 },
        { type: "Labour", days: 3 },
        { type: "Specialist", days: 9 },
    ];
    return (
        <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis label={{ value: "Days", angle: -90, position: "insideLeft" }} />
                <Tooltip />
                <Bar dataKey="days" fill="#1976d2" radius={[6, 6, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
};

export const Dashboard = () => {
    const theme = useTheme();
    return (
        <Box display={'flex'} flexDirection={'column'} height={'100%'} gap={2}>
            <Box display={'flex'} justifyContent={'space-between'} gap={2}>
                <KpiCard title={"Material On-Time Delivery"} value={"33.3%"} subtitle="2 of 6 delivered" variance={"+5.2%"} />
                <KpiCard title={"Resource Fulfilment Rate"} value={"66.7%"} subtitle="4 of 6 fulfilled" variance={"+3.1%"} />
                <KpiCard title={"Material Blockers"} value={"1"} subtitle="Avg lead time: 8.5 days" variance={"-2 from last week"} />
                <KpiCard title={"Resource Shortages"} value={"0"} subtitle="Avg lead time: 5.2 days" variance={"-1 from last week"} />
            </Box>
            <Box display={'flex'} alignItems={'stretch'} gap={2}>
                <DataLayout title="Request Volume Trend">
                    <Box py={3} pr={4}>
                        <RequestVolumeTrend />
                    </Box>
                </DataLayout>
                <DataLayout title="Material Request Status">
                    <Box height={300}>
                        <DonutChart data={[
                            { label: "Ordered", value: 17, color: theme.palette.success.main, legendLabel: "Ordered - 1", legendValue: "17%" },
                            { label: "Delivered", value: 17, color: theme.palette.info.main, legendLabel: "Delivered - 1", legendValue: "17%" },
                            { label: "Used", value: 17, color: theme.palette.error.main, legendLabel: "Used - 1", legendValue: "17%" },
                            { label: "Approved", value: 17, color: theme.palette.warning.dark, legendLabel: "Approved - 1", legendValue: "17%" },
                            { label: "Requested", value: 33, color: theme.palette.secondary.light, legendLabel: "Requested - 2", legendValue: "33%" },
                        ]} />
                    </Box>
                </DataLayout>
            </Box>
            <Box display={'flex'} alignItems={'stretch'} gap={2}>
                <DataLayout title="Resource Request Status">
                    <Box height={300}>
                        <DonutChart data={[
                            { label: "Completed", value: 33, color: theme.palette.info.main, legendLabel: "Completed - 2", legendValue: "33%" },
                            { label: "Requested", value: 17, color: theme.palette.error.main, legendLabel: "Requested - 1", legendValue: "17%" },
                            { label: "Scheduled", value: 17, color: theme.palette.warning.main, legendLabel: "Scheduled - 1", legendValue: "17%" },
                            { label: "Delivered", value: 33, color: theme.palette.success.main, legendLabel: "Delivered - 2", legendValue: "33%" },
                        ]} />
                    </Box>
                </DataLayout>
                <DataLayout title="Average Lead Time by Type">
                    <Box p={4}>
                        <AverageLeadTimeChart />
                    </Box>
                </DataLayout>
            </Box>
        </Box>
    );
};